/**
 * Shared "copy a day to other days" helper (DNKADM-0088) — the pure array
 * operation behind the per-day copy affordance the three weekly grids share
 * (staff schedule, resource schedule, location hours). It carries NO entity
 * knowledge and makes NO request: a caller passes its seven-day local state, a
 * source `dayOfWeek`, and the target `dayOfWeek`s, and gets back a NEW seven-day
 * array with each target overwritten by a DEEP COPY of the source's availability
 * and shifts — each target keeping its OWN `dayOfWeek`. The grid then saves as
 * usual via its existing whole-week PUT (this helper never touches the wire).
 *
 * The three grids' day types (`StaffScheduleDay` / `ResourceScheduleDay` /
 * `WeeklyHoursDay`) are structurally identical here, so the helper is generic
 * over the minimal {@link CopyableDay} shape and preserves any other fields a
 * concrete day type might carry.
 *
 * **Deep copy / no aliasing (required):** the shift objects are CLONED, never
 * shared by reference — after a copy, editing a target day's shift must not
 * mutate the source day. Copying an unavailable source clears the targets'
 * shifts (the source carries none), matching the grids' availability model.
 */
import type { ShiftLike } from './shiftValidation';

/**
 * The minimal day shape {@link copyDay} operates on: a `dayOfWeek` key, an
 * availability flag, and a shift list. The three weekly grids' day types all
 * satisfy this.
 */
export interface CopyableDay {
  dayOfWeek: number;
  isAvailable: boolean;
  shifts: ShiftLike[];
}

/**
 * Return a NEW `days` array with each target `dayOfWeek` overwritten by a deep
 * copy of the source day's `isAvailable` + (cloned) `shifts`, preserving each
 * target's own `dayOfWeek` and any other fields. The source day and unselected
 * days are returned unchanged. The source is never overwritten even if it
 * appears in `targetDows`; an unknown `sourceDow` is a no-op (the original
 * array is returned).
 */
export function copyDay<D extends CopyableDay>(
  days: D[],
  sourceDow: number,
  targetDows: readonly number[],
): D[] {
  const source = days.find((d) => d.dayOfWeek === sourceDow);
  if (source === undefined) return days;
  const targets = new Set(targetDows);
  return days.map((day): D => {
    if (day.dayOfWeek === sourceDow || !targets.has(day.dayOfWeek)) return day;
    return {
      ...day,
      isAvailable: source.isAvailable,
      // Clone each shift object — never share the source's references, so a later
      // edit to a target shift cannot mutate the source (deep copy / no aliasing).
      shifts: source.shifts.map((shift) => ({ ...shift })),
    };
  });
}
